import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchDailyQuestionDetail,
  fetchDailyQuestions,
  fetchTodayDailyQuestion,
  submitTodayDailyQuestionAnswer,
} from '../services/dailyQuestionService';

export function useDailyQuestions({ page = 1, perPage = 20, language = 'EN' } = {}) {
  return useQuery({
    queryKey: ['dailyQuestions', Number(page), Number(perPage), language],
    queryFn: async () => {
      const payload = await fetchDailyQuestions({ page, perPage, language });
      const pagination = payload?.pagination || {};

      return {
        questions: Array.isArray(payload?.daily_questions) ? payload.daily_questions : [],
        pagination: {
          currentPage: Number(pagination?.currentPage || page || 1),
          totalPages: Number(pagination?.totalPages || 1),
          totalRecords: Number(pagination?.totalRecords || 0),
          perPage: Number(pagination?.perPage || perPage || 20),
        },
      };
    },
    placeholderData: (previousData) => previousData,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

export function useDailyQuestionDetail({ dailyQuestionId, language = 'EN' } = {}) {
  return useQuery({
    queryKey: ['dailyQuestionDetail', Number(dailyQuestionId), language],
    queryFn: () => fetchDailyQuestionDetail({ dailyQuestionId, language }),
    enabled: Number(dailyQuestionId) > 0,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

export function useTodayDailyQuestion(language = 'EN', options = {}) {
  return useQuery({
    queryKey: ['todayDailyQuestion', language],
    queryFn: () => fetchTodayDailyQuestion({ language }),
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

export function useSubmitTodayDailyQuestion(language = 'EN') {
  const queryClient = useQueryClient();
  const queryKey = ['todayDailyQuestion', language];

  return useMutation({
    mutationFn: submitTodayDailyQuestionAnswer,
    onSuccess: (payload, variables) => {
      queryClient.setQueryData(queryKey, (current) => {
        if (!current) return current;

        const selectedOptionId = Number(variables?.user_answer_id || 0);
        return {
          ...current,
          is_question_answered: 1,
          attempted_option_id: selectedOptionId,
          is_answer_correct: payload?.is_option_correct ?? current.is_answer_correct,
          question_option: Array.isArray(current.question_option)
            ? current.question_option.map((option) => ({
                ...option,
                user_selected_option: Number(option?.option_id) === selectedOptionId ? 1 : 0,
              }))
            : [],
        };
      });
    },
  });
}
